import React, { useState, useEffect } from 'react';
import { Card, CardBody, Col, Row, Button } from 'reactstrap';
import Chart from 'react-google-charts';
import ReactApexChart from 'react-apexcharts';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux-toolkit/store';

const Classification = () => {
  const [barData, setBarData] = useState<(string | number)[][]>([
    ['Category', 'Class A (%)', 'Class B (%)', 'Class C (%)'],
  ]);
  const [pieData, setPieData] = useState<(string | number)[][]>([
    ['Category', 'Value'],
  ]);
  const [selectedFilter, setSelectedFilter] = useState<'default' | 'position'>('default');
  const [selectedFilters, setSelectedFilters] = useState<{
    months: string[];
    positions: string[];
  }>({
    months: [],
    positions: [],
  });

  const positions = useSelector((state: RootState) => state.position.positions);
  const dateRange = useSelector((state: RootState) => state.position.dateRange);

  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [availablePositions, setAvailablePositions] = useState<string[]>([]);

  useEffect(() => {
    if (positions.length > 0) {
      setAvailablePositions(positions.map((pos) => pos.name));
    }
  }, [positions]);

  useEffect(() => {
    if (dateRange.start && dateRange.end) {
      const startMonth = parseInt(dateRange.start.split('-')[0], 10);
      const startYear = parseInt(dateRange.start.split('-')[1], 10);
      const endMonth = parseInt(dateRange.end.split('-')[0], 10);
      const endYear = parseInt(dateRange.end.split('-')[1], 10);

      const months = [];
      for (let year = startYear; year <= endYear; year++) {
        const monthStart = year === startYear ? startMonth : 1;
        const monthEnd = year === endYear ? endMonth : 12;

        for (let month = monthStart; month <= monthEnd; month++) {
          const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'short' });
          months.push(`${monthName}-${year}`);
        }
      }

      setAvailableMonths(months);

      const updatedBarData = [
        ['Month', 'Class A (%)', 'Class B (%)', 'Class C (%)'],
        ...months.map((month) => {
          return [
            month, // Month as string for x-axis
            Math.floor(Math.random() * 100), // Random percentage for Class A
            Math.floor(Math.random() * 100), // Random percentage for Class B
            Math.floor(Math.random() * 100), // Random percentage for Class C
          ];
        }),
      ];

      setBarData(updatedBarData);
    }
  }, [dateRange]);

  const getFilteredData = () => {
    if (selectedFilters.months.length > 0) {
      // Filter by selected months
      return {
        bar: [
          ['Month', 'Class A (%)', 'Class B (%)', 'Class C (%)'],
          ...selectedFilters.months.map((month) => [
            month,
            Math.random() * 100, // Random value for Class A
            Math.random() * 100, // Random value for Class B
            Math.random() * 100, // Random value for Class C
          ]),
        ],
      };
    } else if (selectedFilters.positions.length > 0) {
      // Filter by selected positions
      return {
        bar: [
          ['Position', 'Class A (%)', 'Class B (%)', 'Class C (%)'],
          ...selectedFilters.positions.map((position) => [
            position,
            Math.random() * 100, // Random value for Class A
            Math.random() * 100, // Random value for Class B
            Math.random() * 100, // Random value for Class C
          ]),
        ],
      };
    } else if (dateRange.start && dateRange.end) {
      // Filter months based on Redux date range
      const startMonth = parseInt(dateRange.start.split('-')[0], 10);
      const startYear = parseInt(dateRange.start.split('-')[1], 10);
      const endMonth = parseInt(dateRange.end.split('-')[0], 10);
      const endYear = parseInt(dateRange.end.split('-')[1], 10);

      const months = [];
      for (let year = startYear; year <= endYear; year++) {
        const monthStart = year === startYear ? startMonth : 1;
        const monthEnd = year === endYear ? endMonth : 12;

        for (let month = monthStart; month <= monthEnd; month++) {
          const monthName = new Date(year, month - 1).toLocaleString('default', { month: 'short' });
          months.push(`${monthName}-${year}`);
        }
      }

      return {
        bar: [
          ['Month', 'Class A (%)', 'Class B (%)', 'Class C (%)'],
          ...months.map((month) => [
            month,
            Math.random() * 100, // Random value for Class A
            Math.random() * 100, // Random value for Class B
            Math.random() * 100, // Random value for Class C
          ]),
        ],
      };
    }

    // Static data for one year (month-wise) if no Redux data is found
    const staticMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const currentYear = new Date().getFullYear();

    return {
      bar: [
        ['Month', 'Class A (%)', 'Class B (%)', 'Class C (%)'],
        ...staticMonths.map((month) => [
          `${month}-${currentYear}`,
          Math.random() * 100, // Random value for Class A
          Math.random() * 100, // Random value for Class B
          Math.random() * 100, // Random value for Class C
        ]),
      ],
    };
  };

  useEffect(() => {
    const filteredBarData = getFilteredData().bar;
    setBarData(filteredBarData);
  }, [selectedFilters]); // Update only bar chart data when filters change

  useEffect(() => {
    const defaultPieData = [
      ['Category', 'Value'],
      ['Class A', Math.random() * 100],
      ['Class B', Math.random() * 100],
      ['Class C', Math.random() * 100],
    ];
    setPieData(defaultPieData);
  }, []); // Set pie chart data once on component mount

  const handleDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value as 'default' | 'position';
    setSelectedFilter(value);

    if (value === 'default') {
      setSelectedFilters({ months: [], positions: [] });
    } else if (value === 'position') {
      setSelectedFilters({ months: [], positions: availablePositions }); // Select all positions
    }
  };

  const barOptions = {
    title: 'Classification Bar Chart',
    vAxis: {
      title: 'Percentage / Value (K Rs)',
      minValue: 0,
      maxValue: 100,
      format: '0%',
      gridlines: { count: 6 },
    },
    hAxis: {
      title: 'Month',
    },
    legend: { position: 'top' },
    bar: { groupWidth: '75%' },
    chartArea: { width: '80%' },
    colors: ['#fe3e3e', '#a5ff65', '#7a7afe'],
    annotations: {
      alwaysOutside: true,
      textStyle: {
        fontSize: 12,
        color: '#000',
      },
      formatter: (value: number) => {
        const percentage = value.toFixed(2); // Format percentage to 2 decimal places
        const valueInKRs = (value * 10).toFixed(2); // Calculate value in K Rs
        return `${valueInKRs} (${percentage}%)`; // Show value and percentage
      },
    },
    // tooltip: {
    //   isHtml: true,
    //   trigger: 'both',
    //   textStyle: {
    //     fontSize: 12,
    //   },
    //   formatter: (value: number, row: any, column: any) => {
    //     const percentage = value.toFixed(2); // Format percentage to 2 decimal places
    //     const valueInKRs = (value * 10).toFixed(2); // Calculate value in K Rs
    //     return `${percentage}% (${valueInKRs} K Rs)`; // Show percentage and value in K Rs
    //   },
    // },
  };

  const pieOptions = {
    title: 'Classification Pie Chart',
    pieHole: 0.4,
    slices: {
      0: { color: '#FF0000' },
      1: { color: '#81c94e' },
      2: { color: '#0000FF' },
    },
    tooltip: {
      isHtml: true,
      trigger: 'both',
      textStyle: {
        fontSize: 12,
      },
      formatter: (value: number, row: any, column: any) => {
        const percentage = value.toFixed(2); // Format percentage to 2 decimal places
        const valueInKRs = (value * 10).toFixed(2); // Calculate value in K Rs
        return `${percentage}% (${valueInKRs} K Rs)`; // Show percentage and value in K Rs
      },
    },
    pieSliceText: 'value&percentage', // Show values and percentages on the pie chart slices
    pieSliceTextStyle: {
      fontSize: 12,
      color: '#000',
    },
    legend: {
      position: 'right',
      textStyle: {
        fontSize: 12,
        color: '#000',
      },
    },
    pieSliceLabel: {
      formatter: (value: number, row: any) => {
        const percentage = ((value / row.total) * 100).toFixed(2);
        const valueInKRs = (value * 10).toFixed(2);
        return `${percentage}% (${valueInKRs} K Rs)`;
      },
    },
  };

  return (
    <Col className="col-sm-12 col-xl-12 box-col-6">
      <Card>
        <CardBody>
          <div className="mb-3">
            <select
              className="form-select"
              value={selectedFilter}
              onChange={handleDropdownChange}
            >
              <option value="default">Default (Month-wise)</option>
              <option value="position">Position (All Positions)</option>
            </select>
          </div>

          <Row>
            <Col md={7}>
              <CardBody className={`chart-block`}>
                <div className="chart-overflow">
                  <ReactApexChart
                    options={{
                      chart: {
                        type: 'area',
                        height: 400,
                        zoom: {
                          enabled: true,
                          type: 'x',
                          autoScaleYaxis: true,
                        },
                        toolbar: {
                          show: true,
                          tools: {
                            zoom: true,
                            zoomin: true,
                            zoomout: true,
                            pan: true,
                            reset: true,
                          },
                        },
                      },
                      title: {
                        text: 'Classification Chart',
                      },
                      xaxis: {
                        categories: barData.slice(1).map((row) => row[0] as string),
                        title: {
                          text: 'Category',
                        },
                      },
                      yaxis: {
                        title: {
                          text: 'Percentage / Value (K Rs)',
                        },
                        min: 0,
                        labels: {
                          formatter: (value) => Math.round(value).toString(), // Ensure integer values only
                        },
                      },
                      colors: ['#FF0000', '#6cbb33', '#0000FF'],
                      legend: {
                        position: 'top',
                      },
                      tooltip: {
                        y: {
                          formatter: (value, { seriesIndex, dataPointIndex, w }) => {
                            if (typeof value === 'number') {
                              const total = w.globals.seriesTotals[seriesIndex] as number;
                              const percentage = total ? ((value / total) * 100).toFixed(2) : '0.00';
                              return `${Math.round(value)} (${percentage}%)`;
                            }
                            return `${value}`; // Convert to string as a fallback
                          },
                        },
                      },
                      dataLabels: {
                        enabled: true,
                        formatter: (value, { seriesIndex, dataPointIndex, w }) => {
                          if (typeof value === 'number') {
                            const total = w.globals.seriesTotals[seriesIndex] as number;
                            const percentage = total ? ((value / total) * 100).toFixed(2) : '0.00';
                            return `${Math.round(value)} (${percentage}%)`;
                          }
                          return `${value}`; // Convert to string as a fallback
                        },
                      },
                    }}
                    series={[
                      {
                        name: 'Class A (%)',
                        data: barData.slice(1).map((row) => Math.round(row[1] as number)),
                      },
                      {
                        name: 'Class B (%)',
                        data: barData.slice(1).map((row) => Math.round(row[2] as number)),
                      },
                      {
                        name: 'Class C (%)',
                        data: barData.slice(1).map((row) => Math.round(row[3] as number)),
                      },
                    ]}
                    type="area"
                    height={400}
                  />
                </div>
              </CardBody>
            </Col>
            <Col md={5}>
              <CardBody className={`chart-block`}>
                <div className="chart-overflow">
                  <Chart
                    width={'100%'}
                    height={'300px'}
                    chartType="PieChart"
                    loader={<div>Loading Chart...</div>}
                    data={pieData}
                    options={{
                      ...pieOptions,
                      is3D: true, // Enable 3D pie chart
                    }}
                  />
                </div>
              </CardBody>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </Col>
  );
};

export default Classification;